import { Map, marker, latLng, tileLayer, control } from 'leaflet';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import * as L from 'leaflet';
import { CustomersService } from '../../_services/customers/customers.service';
import { SessionService } from '../../_services/session/session.service';
import { BaseComponent } from '../../components/base/base.component';
import IPatient from '../../_interfaces/IPatients';
import ISite from '../../_interfaces/ISite';
import { forkJoin, map } from 'rxjs';
import { InputNumberModule } from 'primeng/inputnumber';
import { FormsModule } from '@angular/forms';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { ButtonModule } from 'primeng/button';

function haversine(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

@Component({
  standalone: true,
  selector: 'app-business-analysis',
  templateUrl: './business-analysis.component.html',
  styleUrls: ['./business-analysis.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [InputNumberModule, FormsModule, LeafletModule, ButtonModule],
})
export class BusinessAnalysisComponent extends BaseComponent implements OnInit {
  public map: any;
  public patients: IPatient[];
  public markers: L.Layer[] = [];
  public patientIcon: L.DivIcon;
  public sitesIcon: { id: number; icon: L.DivIcon }[];
  public patientsIcon: { id: number; icon: L.DivIcon }[];

  public patientsMarkers: L.Layer[] = [];
  public sitesMarkers: L.Layer[] = [];
  public suggestionsMarkers: L.Layer[] = [];
  public sites: ISite[];
  public radius: number = 500;

  public patientsLayer: L.LayerGroup = L.layerGroup();
  public sitesLayer: L.LayerGroup = L.layerGroup();
  public suggestionsLayers: L.LayerGroup = L.layerGroup();
  public suggestedSites: { latitude: number; longitude: number }[] = [];
  public controlLayers: L.Control.Layers;

  constructor(
    public session: SessionService,
    public customersService: CustomersService
  ) {
    super(session);
  }

  options = {
    layers: [
      tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '...',
      }),
    ],
    zoom: 10,
    center: latLng(45.8185, 15.98),
  };

  updateLayers() {
    this.updatePatientsLayer();
    this.updateSitesLayer();
    this.updateSuggestionsLayer();
  }

  updateSuggestionsLayer() {
    this.suggestionsLayers.clearLayers();
    this.suggestionsMarkers.forEach((sM) => {
      this.suggestionsLayers.addLayer(sM);
    });

    this.suggestionsLayers.addTo(this.map);
  }

  updateSitesLayer() {
    this.sitesLayer.clearLayers();

    this.sitesMarkers.forEach((sM) => {
      this.sitesLayer.addLayer(sM);
    });

    this.sitesLayer.addTo(this.map);
  }

  updatePatientsLayer() {
    this.patientsLayer.clearLayers();

    this.patientsMarkers.forEach((pM) => {
      this.patientsLayer.addLayer(pM);
    });

    this.patientsLayer.addTo(this.map);
  }

  exectuesBestClinicAlg() {
    this.suggestionsMarkers = [];
    this.suggestedSites = [];

    for (let i = 0; i <= 1; i++) {
      const bestLocation = this.findBestClinicLocation();
      if (bestLocation) {
        this.addNewClinicMarker(bestLocation);
      }
    }
  }

  getSitesMarkers() {
    const sitesCircles = this.sites
      .map((site: ISite) => {
        if (site.latitude && site.longitude)
          return L.circle(new L.LatLng(site.latitude, site.longitude), {
            radius: this.radius,
            fillColor: 'green',
            color: 'green',
          });
        return undefined;
      })
      .filter((x) => x != undefined);

    const sitesMarkers = this.sites
      .map((site: ISite) => {
        if (site.latitude && site.longitude)
          return marker([site.latitude, site.longitude], {
            title: site.address,
            icon: (this.sitesIcon.find((x) => x.id == site.id) || {}).icon,
          });
        return undefined;
      })
      .filter((x) => x != undefined);

    this.sitesMarkers = [...sitesCircles, ...sitesMarkers];
  }

  getPatientsMarkers() {
    this.patientsMarkers = this.patients
      .map((patient: IPatient) => {
        if (patient.latitude && patient.longitude) {
          const mainSite =
            patient.siteConnections?.find((x) => x.is_main_site) ||
            patient.siteConnections
              ? patient.siteConnections[0]
              : undefined;

          return marker([patient.latitude, patient.longitude], {
            title: patient.address,
            icon: mainSite
              ? this.patientsIcon.find((x) => x.id == mainSite.sites_id)?.icon
              : undefined,
          });
        }
        return undefined;
      })
      .filter((x) => x != undefined);
  }

  // Método auxiliar para evaluar la cobertura en una ubicación específica
  evaluateLocation(
    lat: number,
    lng: number,
    coveredPatients: Set<IPatient>,
    radiusKm: number,
    callback: (coverage: number, location: L.LatLng) => void
  ) {
    let coverage = 0;
    const testLocation = L.latLng(lat, lng);

    // Verificar si el sitio está demasiado cerca de otros sitios
    const isTooCloseToSite = [...this.sites, ...this.suggestedSites];

    isTooCloseToSite.some((site) => {
      const distanceToSite = haversine(
        lat,
        lng,
        site.latitude as number,
        site.longitude as number
      );
      return distanceToSite <= radiusKm;
    });

    if (isTooCloseToSite) {
      return; // Saltar ubicaciones demasiado cercanas
    }

    // Contar los pacientes no cubiertos previamente
    this.patients.forEach((patient) => {
      const distanceToPatient = haversine(
        lat,
        lng,
        patient.latitude as number,
        patient.longitude as number
      );
      if (distanceToPatient <= radiusKm && !coveredPatients.has(patient)) {
        coverage++;
      }
    });

    callback(coverage, testLocation);
  }

  findBestClinicLocation(): L.LatLng {
    let bestLocation: L.LatLng = new L.LatLng(0, 0);
    let minCoverage = Infinity; // Queremos encontrar la ubicación con la menor cobertura
    const radiusKm = this.radius / 1000;
    const searchBounds = this.map.getBounds();

    // Configuraciones para las divisiones de las columnas y filas
    const numColumns = 30; // Configurable: número de columnas a dividir el mapa
    const numRows = 10; // Configurable: número de filas en cada columna
    const coveredPatients = new Set<IPatient>();

    // Calcular el rango de latitud y longitud del área
    const latRange =
      searchBounds.getNorthEast().lat - searchBounds.getSouthWest().lat;
    const lngRange =
      searchBounds.getNorthEast().lng - searchBounds.getSouthWest().lng;

    // Calcular el tamaño del paso para latitud y longitud
    const latStep = latRange / numRows;
    const lngStep = lngRange / numColumns;

    // Marcar todos los pacientes que ya están cubiertos por sitios existentes
    this.sites.forEach((site) => {
      this.patients.forEach((patient) => {
        const distanceToSite = haversine(
          site.latitude as number,
          site.longitude as number,
          patient.latitude as number,
          patient.longitude as number
        );
        if (distanceToSite <= radiusKm) {
          coveredPatients.add(patient);
        }
      });
    });

    // Iterar sobre cada columna
    for (let col = 0; col < numColumns; col++) {
      const currentLng = searchBounds.getSouthWest().lng + col * lngStep;

      // Alternar dirección de la fila en cada columna (zigzag)
      const isZigzagDown = col % 2 === 1;

      if (isZigzagDown) {
        // Bajando: desde el norte al sur
        for (let row = numRows - 1; row >= 0; row--) {
          const currentLat = searchBounds.getSouthWest().lat + row * latStep;
          this.evaluateLocation(
            currentLat,
            currentLng,
            coveredPatients,
            radiusKm,
            (coverage, location) => {
              if (coverage < minCoverage) {
                minCoverage = coverage;
                bestLocation = location;
              }
            }
          );
        }
      } else {
        // Subiendo: desde el sur al norte
        for (let row = 0; row < numRows; row++) {
          const currentLat = searchBounds.getSouthWest().lat + row * latStep;
          this.evaluateLocation(
            currentLat,
            currentLng,
            coveredPatients,
            radiusKm,
            (coverage, location) => {
              if (coverage < minCoverage) {
                minCoverage = coverage;
                bestLocation = location;
              }
            }
          );
        }
      }
    }

    if (bestLocation.lat && bestLocation.lng) {
      // Añadir la mejor ubicación a los sitios sugeridos y actualizar el mapa
      this.suggestedSites.push({
        latitude: (bestLocation as L.LatLng).lat,
        longitude: (bestLocation as L.LatLng).lng,
      });
    }

    return bestLocation;
  }

  addNewClinicMarker(location: L.LatLng) {
    const suggestedClinicMarker = L.marker(location, {
      title: 'Propuesta de nueva clínica',
      icon: L.divIcon({
        className: 'custom-marker custom-clinic-marker custom-proposal',
        html: '<i class="fas fa-map-marker-alt"></i>',
        iconSize: [50, 82],
        popupAnchor: [0, -20],
      }),
    });

    const suggestedClinicCircle = L.circle(location, { radius: this.radius });

    this.suggestionsMarkers = this.suggestionsMarkers.concat([
      suggestedClinicMarker,
      suggestedClinicCircle,
    ]);
  }

  onMapReady(mapInstance: Map) {
    this.map = mapInstance;
    this.controlLayers = control
      .layers(
        {},
        {
          Sitios: this.sitesLayer,
          Pacientes: this.patientsLayer,
          Suggestions: this.suggestionsLayers,
        }
      )
      .addTo(this.map);
  }

  recalculate() {
    this.exectuesBestClinicAlg();
    this.getSitesMarkers();
    this.updateSitesLayer();
    this.updateSuggestionsLayer();
  }

  loadInformation() {
    forkJoin({
      patients: this.customersService.getPatients(this.customers_id),
      sites: this.customersService.getSites(this.customers_id),
    })
      .pipe(
        map(({ patients, sites }) => {
          this.patients = patients;
          this.sites = sites;

          this.getPatientsMarkers();
          this.getSitesMarkers();
          this.exectuesBestClinicAlg();
          this.updateLayers();
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.sitesIcon = [
      {
        id: 1,
        icon: L.divIcon({
          className: 'custom-marker custom-site-marker marker-site-1',
          html: '<i class="fas fa-map-marker-alt"></i>',
          iconSize: [50, 82],
          popupAnchor: [0, -20],
        }),
      },
      {
        id: 2,
        icon: L.divIcon({
          className: 'custom-marker custom-site-marker marker-site-2',
          html: '<i class="fas fa-map-marker-alt"></i>',
          iconSize: [50, 82],
          popupAnchor: [0, -20],
        }),
      },
      {
        id: 3,
        icon: L.divIcon({
          className: 'custom-marker custom-site-marker marker-site-3',
          html: '<i class="fas fa-map-marker-alt"></i>',
          iconSize: [50, 82],
          popupAnchor: [0, -20],
        }),
      },
      {
        id: 4,
        icon: L.divIcon({
          className: 'custom-marker custom-site-marker marker-site-4',
          html: '<i class="fas fa-map-marker-alt"></i>',
          iconSize: [50, 82],
          popupAnchor: [0, -20],
        }),
      },
      {
        id: 15,
        icon: L.divIcon({
          className: 'custom-marker custom-site-marker marker-site-4',
          html: '<i class="fas fa-map-marker-alt"></i>',
          iconSize: [50, 82],
          popupAnchor: [0, -20],
        }),
      },
      {
        id: 16,
        icon: L.divIcon({
          className: 'custom-marker custom-site-marker marker-site-4',
          html: '<i class="fas fa-map-marker-alt"></i>',
          iconSize: [50, 82],
          popupAnchor: [0, -20],
        }),
      },
    ];

    this.patientsIcon = [
      {
        id: 1,
        icon: L.divIcon({
          className: 'custom-marker custom-patient-marker marker-site-1',
          html: '<i class="fas fa-map-marker-alt"></i>',
          iconSize: [50, 82],
          popupAnchor: [0, -20],
        }),
      },
      {
        id: 2,
        icon: L.divIcon({
          className: 'custom-marker custom-patient-marker marker-site-2',
          html: '<i class="fas fa-map-marker-alt"></i>',
          iconSize: [50, 82],
          popupAnchor: [0, -20],
        }),
      },
      {
        id: 3,
        icon: L.divIcon({
          className: 'custom-marker custom-patient-marker marker-site-3',
          html: '<i class="fas fa-map-marker-alt"></i>',
          iconSize: [50, 82],
          popupAnchor: [0, -20],
        }),
      },
      {
        id: 4,
        icon: L.divIcon({
          className: 'custom-marker custom-patient-marker marker-site-4',
          html: '<i class="fas fa-map-marker-alt"></i>',
          iconSize: [50, 82],
          popupAnchor: [0, -20],
        }),
      },
      {
        id: 15,
        icon: L.divIcon({
          className: 'custom-marker custom-patient-marker marker-site-4',
          html: '<i class="fas fa-map-marker-alt"></i>',
          iconSize: [50, 82],
          popupAnchor: [0, -20],
        }),
      },

      {
        id: 16,
        icon: L.divIcon({
          className: 'custom-marker custom-patient-marker marker-site-4',
          html: '<i class="fas fa-map-marker-alt"></i>',
          iconSize: [50, 82],
          popupAnchor: [0, -20],
        }),
      },
    ];

    super.ngOnInit();

    this.loadInformation();
  }
}
